"use client";

import { useEffect, useRef, useState } from "react";
import type { FormikProps } from "formik";
import { Check, ChevronDown, Plus, Trash2 } from "@/components/icons";
import { FormError } from "@/components/ui";
import { REPORT_ABUSE_TYPES } from "@/lib/constants";
import type { ReportAbuseFormValues } from "./types";

const inputClass =
  "w-full rounded-lg border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 transition-colors";
/* Foreground-alpha rather than white-alpha: identical on the dark theme, and a
   faint grey (not an invisible white-on-white) once the tokens flip to light. */
const inputStyle = { backgroundColor: "color-mix(in srgb, var(--foreground) 3%, transparent)" };

interface ReportTypeSelectProps {
  formId: string;
  formik: FormikProps<ReportAbuseFormValues>;
}

export function ReportTypeSelect({ formId, formik }: ReportTypeSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerId = `${formId}-reportType-trigger`;
  const labelId = `${formId}-reportType-label`;
  const listboxId = `${formId}-reportType-listbox`;

  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        document.getElementById(triggerId)?.focus();
      }
    }
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, triggerId]);

  const selected = REPORT_ABUSE_TYPES.find((type) => type.value === formik.values.reportType);
  const error = formik.touched.reportType && typeof formik.errors.reportType === "string";

  return (
    <div>
      <label
        id={labelId}
        className="text-muted-foreground mb-2 block text-xs font-semibold tracking-wider uppercase"
      >
        Type of Report <span className="text-red-400">*</span>
      </label>
      <div ref={containerRef} className="relative">
        <button
          type="button"
          id={triggerId}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-labelledby={`${labelId} ${triggerId}`}
          onClick={() => setOpen((o) => !o)}
          onBlur={() => formik.setFieldTouched("reportType", true)}
          className={`${inputClass} flex items-center justify-between`}
          style={inputStyle}
        >
          <span className={selected ? "text-foreground" : "text-muted-foreground"}>
            {selected ? selected.label : "Select a report type"}
          </span>
          <ChevronDown
            size={14}
            aria-hidden="true"
            className="text-muted-foreground shrink-0 transition-transform duration-200"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>

        {open && (
          <ul
            id={listboxId}
            role="listbox"
            aria-labelledby={labelId}
            className="border-border bg-card absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border shadow-lg"
          >
            {REPORT_ABUSE_TYPES.map(({ value, label }) => (
              <li key={value} role="option" aria-selected={formik.values.reportType === value}>
                <button
                  type="button"
                  onClick={() => {
                    formik.setFieldValue("reportType", value);
                    formik.setFieldTouched("reportType", true);
                    setOpen(false);
                  }}
                  className="text-foreground hover:bg-muted block w-full px-3 py-2.5 text-left text-sm transition-colors"
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <FormError className="mt-1">{formik.errors.reportType}</FormError>}

      {formik.values.reportType === "dmca" && (
        <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
          This is also the channel referenced in our Terms and Conditions and Acceptable Use Policy.
          Please include the specific infringing link or links in the destination field below.
        </p>
      )}
      {formik.values.reportType === "account_security" && (
        <p className="text-muted-foreground mt-2 text-xs leading-relaxed">
          Use this option if you are the Proxidize account holder reporting that your own account
          has been compromised. Evidence of Abuse is optional for this report type.
        </p>
      )}
    </div>
  );
}

interface DestinationUrlsFieldProps {
  formId: string;
  formik: FormikProps<ReportAbuseFormValues>;
}

const MAX_DESTINATION_URLS = 10;

export function DestinationUrlsField({ formId, formik }: DestinationUrlsFieldProps) {
  const urls = formik.values.destinationUrls;
  const errors = Array.isArray(formik.errors.destinationUrls) ? formik.errors.destinationUrls : [];

  const nextRowId = useRef(urls.length);
  const [rowIds, setRowIds] = useState<number[]>(() => urls.map((_, i) => i));

  function updateUrl(index: number, value: string) {
    const next = [...urls];
    next[index] = value;
    formik.setFieldValue("destinationUrls", next);
  }

  function removeUrl(index: number) {
    formik.setFieldValue(
      "destinationUrls",
      urls.filter((_, i) => i !== index)
    );
    setRowIds((prev) => prev.filter((_, i) => i !== index));
  }

  function addUrl() {
    if (urls.length >= MAX_DESTINATION_URLS) return;
    formik.setFieldValue("destinationUrls", [...urls, ""]);
    setRowIds((prev) => [...prev, nextRowId.current++]);
  }

  return (
    <div>
      <label
        htmlFor={`${formId}-destinationUrl-0`}
        className="text-muted-foreground mb-2 block text-xs font-semibold tracking-wider uppercase"
      >
        Destination Domain or URL Affected
      </label>
      <p className="text-muted-foreground mb-2 text-xs">
        The site or service the reported IP was accessing or targeting, if known.
      </p>

      <div className="flex flex-col gap-2">
        {urls.map((url, index) => {
          const fieldError = typeof errors[index] === "string" ? errors[index] : undefined;
          return (
            <div key={rowIds[index] ?? index} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <input
                  id={`${formId}-destinationUrl-${index}`}
                  name={`destinationUrls.${index}`}
                  type="text"
                  placeholder="example.com or https://example.com/page"
                  value={url}
                  onChange={(e) => updateUrl(index, e.target.value)}
                  onBlur={formik.handleBlur}
                  className={inputClass}
                  style={inputStyle}
                />
                {urls.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrl(index)}
                    className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                    aria-label={`Remove destination URL ${index + 1}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              {fieldError && <FormError>{fieldError}</FormError>}
            </div>
          );
        })}
      </div>

      {urls.length < MAX_DESTINATION_URLS && (
        <button
          type="button"
          onClick={addUrl}
          className="text-muted-foreground hover:text-foreground mt-2 flex items-center gap-1.5 text-xs font-semibold transition-colors"
        >
          <Plus size={13} />
          Add link
        </button>
      )}
    </div>
  );
}

interface AccuracyCheckboxProps {
  formId: string;
  formik: FormikProps<ReportAbuseFormValues>;
}

export function AccuracyCheckbox({ formId, formik }: AccuracyCheckboxProps) {
  const error = formik.touched.attestation && typeof formik.errors.attestation === "string";

  return (
    <div>
      <label className="group flex cursor-pointer items-start gap-3">
        <div className="relative mt-0.5 shrink-0">
          <input
            id={`${formId}-attestation`}
            type="checkbox"
            name="attestation"
            checked={formik.values.attestation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="sr-only"
          />
          <div
            className="flex h-4 w-4 items-center justify-center rounded border transition-all duration-150"
            style={{
              borderColor: formik.values.attestation
                ? "var(--foreground)"
                : "color-mix(in srgb, var(--foreground) 20%, transparent)",
              backgroundColor: formik.values.attestation ? "var(--foreground)" : "transparent",
            }}
          >
            {formik.values.attestation && (
              <Check size={11} className="text-background" strokeWidth={3} />
            )}
          </div>
        </div>
        <span className="text-muted-foreground text-xs leading-relaxed">
          I confirm that the information provided in this report is accurate to the best of my
          knowledge.
        </span>
      </label>
      {error && <FormError className="mt-1">{formik.errors.attestation}</FormError>}
    </div>
  );
}
